// frontend/src/pages/ForgotPassword.tsx
import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { buildApiUrl, API_CONFIG } from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import { useCooldown } from '../hooks/useCooldown';
import { resolveAfterCredential } from '../utils/returnTo';
import ParticleBackground from '../components/ParticleBackground';
import { Eye, EyeOff } from 'lucide-react';

import { newPasswordError, PASSWORD_RULES_HINT } from '../utils/passwordPolicy';

const ForgotPassword = () => {
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  // Contador local, e local é o único jeito possível aqui: o
  // `/auth/forgot-password` devolve a MESMA string genérica no cooldown e no
  // caminho normal, e NÃO manda `retryInSeconds` — uma resposta distinta viraria
  // oráculo de "esta conta pediu reset agora". Então a tela conta os 60 s por
  // conta própria e o backend silenciosamente não reenvia dentro da janela.
  const [cooldownLeft, setCooldownLeft] = useCooldown();

  /**
   * Pede o código ao backend. Serve ao passo 1 e ao reenvio do passo 2 — é a
   * mesma chamada, e duplicá-la deixaria os dois caminhos divergindo no
   * tratamento de erro e no cooldown.
   */
  const requestCode = async (opts: { reenvio: boolean }) => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.FORGOT_PASSWORD), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Erro ao solicitar código');
      }

      setSuccess(
        opts.reenvio
          ? 'Código reenviado! Use o mais recente — o anterior deixou de valer.'
          : 'Código enviado! Verifique seu e-mail.',
      );
      // 61 e não 60: a janela do backend conta da EMISSÃO, alguns ms depois
      // desta resposta, então liberar em 60,0s ainda bateria no cooldown.
      setCooldownLeft(61);
      setStep('code');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao solicitar código');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    await requestCode({ reenvio: false });
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const passwordError = newPasswordError(newPassword, confirmPassword);
    if (passwordError) {
      setError(passwordError);
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.RESET_PASSWORD), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          code,
          newPassword,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        // NestJS ValidationPipe retorna message como array; normaliza p/ string.
        const msg = Array.isArray(data.message)
          ? data.message.join(' • ')
          : data.message;
        throw new Error(msg || 'Erro ao redefinir senha');
      }

      // O backend já autentica no reset (mesmo contrato do /auth/login), então
      // não há por que mandar o usuário redigitar a senha que ele acabou de
      // definir.
      if (data.access_token) {
        setSuccess('Senha redefinida com sucesso! Entrando...');
        await login(data.access_token, data.user);
        navigate(resolveAfterCredential(location.state?.from), { replace: true });
        return;
      }

      // Sem token: é o ramo de e-mail inexistente, que responde 200 com a
      // mensagem genérica para não virar oráculo de enumeração. Mantém o
      // comportamento antigo — nada a fazer além de mandar para o login.
      setSuccess('Senha redefinida com sucesso! Redirecionando...');
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao redefinir senha');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToEmail = () => {
    setStep('email');
    setCode('');
    setNewPassword('');
    setConfirmPassword('');
    setError('');
    setSuccess('');
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-secondary-950 via-primary-950 to-secondary-950 px-4 py-10">
      {/* Particles em fullscreen, com fundo transparente pra deixar o gradiente passar */}
      <ParticleBackground backgroundColor="transparent" />

      {/* Glows decorativos — azul profundo + toque de amarelo da identidade */}
      <div className="pointer-events-none absolute -top-40 -left-40 w-[28rem] h-[28rem] rounded-full bg-primary-700/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 -right-40 w-[28rem] h-[28rem] rounded-full bg-amber-500/10 blur-3xl" />

      <div className="relative z-10 w-full max-w-md">
        <div className="border border-white/10 bg-secondary-950/70 backdrop-blur-xl rounded-xl p-10 text-center shadow-2xl shadow-primary-950/40 animate-slide-up">
          <img 
            src="/logo-codifica.png" 
            alt="Logo" 
            className="max-w-64 mx-auto mb-5"
          />
          
          <h1 className="text-white text-2xl font-bold mb-8">
            {step === 'email' ? 'Recuperar Senha' : 'Redefinir Senha'}
          </h1>

          {step === 'email' ? (
            <form onSubmit={handleRequestCode} className="space-y-6">
              {error && (
                <div className="bg-red-900 bg-opacity-20 border border-red-500 text-red-400 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}
              {success && (
                <div className="bg-green-900 bg-opacity-20 border border-green-500 text-green-400 px-4 py-3 rounded-lg text-sm">
                  {success}
                </div>
              )}

              <div className="relative">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-3 rounded-lg bg-secondary-900/80 border border-secondary-700 text-white placeholder-secondary-500 focus:border-amber-400 focus:ring-2 focus:ring-amber-400/30 focus:outline-none transition-all duration-200"
                  placeholder="Digite seu e-mail"
                  autoComplete="email"
                />
                <i className="fas fa-envelope absolute right-4 top-1/2 transform -translate-y-1/2 text-secondary-500 text-lg"></i>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 px-6 rounded-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {loading ? (
                  <>Enviando... <i className="fas fa-spinner fa-spin ml-2"></i></>
                ) : (
                  <>Enviar Código <i className="fas fa-paper-plane ml-2"></i></>
                )}
              </button>
            </form>
          ) : (
            <form onSubmit={handleResetPassword} className="space-y-6">
              {error && (
                <div className="bg-red-900 bg-opacity-20 border border-red-500 text-red-400 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}
              {success && (
                <div className="bg-green-900 bg-opacity-20 border border-green-500 text-green-400 px-4 py-3 rounded-lg text-sm">
                  {success}
                </div>
              )}

              <div className="relative">
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  maxLength={6}
                  className="w-full px-4 py-4 pr-12 border border-gray-600 rounded-lg bg-black bg-opacity-60 text-white placeholder-subtle focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-30 transition-all duration-300 text-center text-2xl tracking-widest"
                  placeholder="000000"
                />
                <i className="fas fa-key absolute right-4 top-1/2 transform -translate-y-1/2 text-blue-500 text-lg"></i>
              </div>

              <button
                type="button"
                onClick={() => requestCode({ reenvio: true })}
                disabled={loading || cooldownLeft > 0}
                className="text-xs text-blue-400 hover:text-blue-300 disabled:opacity-50 disabled:cursor-not-allowed text-left"
              >
                {cooldownLeft > 0
                  ? `Aguarde ${cooldownLeft}s para reenviar`
                  : 'Não recebeu ou o código expirou? Reenviar código'}
              </button>

              <div>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    className="w-full px-4 py-4 pr-16 border border-gray-600 rounded-lg bg-black bg-opacity-60 text-white placeholder-subtle focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-30 transition-all duration-300"
                    placeholder="Nova senha"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-blue-500 hover:text-blue-400"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <p className="text-xs text-subtle mt-2 text-left">{PASSWORD_RULES_HINT}</p>
              </div>

              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="w-full px-4 py-4 pr-16 border border-gray-600 rounded-lg bg-black bg-opacity-60 text-white placeholder-subtle focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-30 transition-all duration-300"
                  placeholder="Confirmar senha"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-blue-500 hover:text-blue-400"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleBackToEmail}
                  className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-bold py-4 px-6 rounded-lg transition-all duration-300"
                >
                  Voltar
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 px-6 rounded-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>Redefinindo... <i className="fas fa-spinner fa-spin ml-2"></i></>
                  ) : (
                    <>Redefinir</>
                  )}
                </button>
              </div>
            </form>
          )}

          <div className="mt-6">
            <button
              onClick={() => navigate('/login')}
              className="text-blue-500 hover:text-blue-400 font-bold transition-colors duration-300"
             type="button">
              Voltar para o login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;